import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Link,
} from "@react-pdf/renderer";
import { formatDateEmail, getDayInGerman } from "../../utils/DataFormatter";
import {
  extractCurrency,
  formatCurrencySpacing,
  reverseWords,
} from "../../utils/Utils";

// A sample set of styles, similar to the PDF example.
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    fontFamily: "Helvetica", // or register a custom font
  },
  section: {
    marginBottom: 10,
  },
  title: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 15,
  },
  headingSmall: {
    fontWeight: "bold",
    marginBottom: 4,
    marginTop: 15,
  },
  bold: {
    fontWeight: "bold",
  },
  // Table-like containers
  tableContainer: {
    marginVertical: 5,
    borderWidth: 1,
    borderColor: "#000",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f2f2f2",
    borderBottomWidth: 1,
    borderColor: "#000",
  },
  tableHeaderCell: {
    flex: 1,
    fontWeight: "bold",
    padding: 3,
    borderRightWidth: 1,
    borderColor: "#000",
  },
  tableHeaderCellLast: {
    flex: 1,
    fontWeight: "bold",
    padding: 3,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderColor: "#000",
  },
  tableCell: {
    flex: 1,
    padding: 3,
    borderRightWidth: 1,
    borderColor: "#000",
  },
  tableCellLast: {
    flex: 1,
    padding: 3,
  },
  tableFooterRow: {
    flexDirection: "row",
  },
  // For multi-column layout, e.g. addresses
  twoColumnGrid: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 10,
    marginBottom: 20,
  },
  column: {
    width: "48%",
  },
  textSmall: {
    fontSize: 10,
    marginBottom: 2,
  },
});

const CourseInfo = ({ title, price }: { title: string; price: string }) => {
  const priceWithCurrency = reverseWords(formatCurrencySpacing(price));
  return (
    <View style={styles.section}>
      <Text style={styles.headingSmall}>Zusammenfassung</Text>
      <View style={styles.tableContainer}>
        {/* Header */}
        <View style={styles.tableHeader}>
          <Text style={styles.tableHeaderCell}>Bezeichnung</Text>
          <Text style={styles.tableHeaderCell}>Preis</Text>
          <Text style={styles.tableHeaderCell}>Anzahl</Text>
          <Text style={styles.tableHeaderCellLast}>Total</Text>
        </View>

        {/* Row */}
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>{title}</Text>
          <Text style={styles.tableCell}>{priceWithCurrency}.00 </Text>
          <Text style={styles.tableCell}>1</Text>
          <Text style={styles.tableCellLast}>{priceWithCurrency}.00</Text>
        </View>

        {/* Row: MwSt */}
        <View style={styles.tableRow}>
          <Text style={[styles.tableCell, { flex: 3, textAlign: "right" }]}>
            Nicht MwSt-pflichtig
          </Text>
          <Text style={styles.tableCellLast}>
            {extractCurrency(price)} 0.00
          </Text>
        </View>

        {/* Footer row: Total */}
        <View style={styles.tableFooterRow}>
          <View style={[styles.tableCell, { flex: 3, textAlign: "right" }]}>
            <Text style={styles.bold}>Total ({extractCurrency(price)})</Text>
          </View>
          <View style={styles.tableCellLast}>
            <Text>{priceWithCurrency}.00</Text>
          </View>
        </View>
      </View>
    </View>
  );
};

const DriverDetails = ({
  firstName,
  lastName,
  email,
}: {
  firstName: string;
  lastName: string;
  email: string;
}) => {
  return (
    <View style={styles.section}>
      <Text style={styles.headingSmall}>Teilnehmer</Text>
      <View style={styles.tableContainer}>
        {/* Header */}
        <View style={styles.tableHeader}>
          <Text style={styles.tableHeaderCell}>Nr.</Text>
          <Text style={styles.tableHeaderCell}>Name</Text>
          <Text style={styles.tableHeaderCell}>Vorname</Text>
          <Text style={styles.tableHeaderCellLast}>Email</Text>
        </View>

        {/* Row */}
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>1</Text>
          <Text style={styles.tableCell}>{firstName}</Text>
          <Text style={styles.tableCell}>{lastName}</Text>
          <Text style={styles.tableCellLast}>{email}</Text>
        </View>
      </View>
    </View>
  );
};

const CourseDateRow = ({
  date,
  startTime,
  endTime,
}: {
  date: string;
  startTime: string;
  endTime: string;
}) => {
  return (
    <View style={styles.tableRow}>
      <Text style={styles.tableCell}>{getDayInGerman(date)}</Text>
      <Text style={[styles.tableCell, { flex: 3 }]}>
        {date} {startTime} - {endTime}
      </Text>
      {/* We'll fill in "Raum" and "Ort" in the parent component */}
    </View>
  );
};

const CourseDateTime = ({
  type,
  title,
  data,
}: {
  type: "vku" | "plus" | null;
  title: string;
  data: any;
}) => {
  const isVKU = type === "vku";

  return (
    <View style={styles.section}>
      <Text style={styles.headingSmall}>Kurs: {title}</Text>
      {/* First summary row */}
      <View style={styles.tableContainer}>
        <View style={styles.tableHeader}>
          <Text style={styles.tableHeaderCell}>Dauer</Text>
          <Text style={[styles.tableHeaderCell, { flex: 3 }]}>Datum</Text>
          <Text style={styles.tableHeaderCellLast}>Wochentag</Text>
        </View>

        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>
            {isVKU
              ? "2 Tag(e) \n Total 2 Lektionen"
              : "3 Tag(e) \n Total 3 Lektionen"}
          </Text>
          <Text style={[styles.tableCell, { flex: 3 }]}>
            {data?.fDate} - {isVKU ? data?.sDate : data?.tDate}
          </Text>
          <Text style={styles.tableCellLast}>
            {getDayInGerman(data?.fDate)}, {getDayInGerman(data?.sDate)}
            {data?.tDate ? ", " + getDayInGerman(data?.tDate) : ""}
          </Text>
        </View>
      </View>

      {/* Detailed rows */}
      <View style={[styles.tableContainer, { marginTop: 5 }]}>
        <View style={styles.tableHeader}>
          <Text style={styles.tableHeaderCell}>Tag</Text>
          <Text style={[styles.tableHeaderCell, { flex: 3 }]}>Datum, Zeit</Text>
          <Text style={styles.tableHeaderCell}>Raum</Text>
          <Text style={styles.tableHeaderCellLast}>Ort</Text>
        </View>

        {/* First day */}
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>{getDayInGerman(data?.fDate)}</Text>
          <Text style={[styles.tableCell, { flex: 3 }]}>
            {data?.fDate} {data?.fStartTime} - {data?.fEndTime}
          </Text>
          <Text style={styles.tableCell}>Fahrschule Lumi</Text>
          <Text style={styles.tableCellLast}>
            Winterthur City Römerstrasse 76 8404 Winterthur
          </Text>
        </View>

        {/* Second day */}
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>{getDayInGerman(data?.sDate)}</Text>
          <Text style={[styles.tableCell, { flex: 3 }]}>
            {data?.sDate} {data?.sStartTime} - {data?.sEndTime}
          </Text>
          {/* If you want the same "Fahrschule Lumi" detail, replicate it */}
          <Text style={styles.tableCell}>-</Text>
          <Text style={styles.tableCellLast}>-</Text>
        </View>

        {/* Third day (if applicable) */}
        {data?.tDate && (
          <View style={styles.tableRow}>
            <Text style={styles.tableCell}>{getDayInGerman(data?.tDate)}</Text>
            <Text style={[styles.tableCell, { flex: 3 }]}>
              {data?.tDate} {data?.tStartTime} - {data?.tEndTime}
            </Text>
            <Text style={styles.tableCell}>-</Text>
            <Text style={styles.tableCellLast}>-</Text>
          </View>
        )}
      </View>
    </View>
  );
};

const Footer = ({ type }: { type: "vku" | "plus" | null }) => {
  return (
    <View style={styles.section}>
      <Text style={[styles.bold, { marginBottom: 2, marginTop: 8 }]}>
        Kursgeld:{" "}
      </Text>
      <Text>
        Bitte das Kursgeld CHF {type === "vku" ? "140" : "130"}.- am 1.Kurstag
        bar mitbringen.
      </Text>
      <Text style={[styles.bold, { marginBottom: 2, marginTop: 8 }]}>
        Pünktliches Erscheinen:{" "}
      </Text>
      <Text>
        Zu spätes Eintreffen am Kursort hat in jedem Fall die Verrechnung des
        ganzen Kursgeldes zur Folge.
      </Text>
      <Text style={[styles.bold, { marginBottom: 2, marginTop: 8 }]}>
        Abmeldung:
      </Text>
      <Text style={{ marginBottom: 8 }}>
        Abmeldungen sind nur schriftlich per SMS unter:{" "}
        <Link href="tel:+41764727563" style={{ color: "#2563eb" }}>
          +41 76 472 75 63
        </Link>{" "}
        oder email:{" "}
        <Link
          href="mailto:kontakt@fahrschul-kurse.ch"
          style={{ color: "#2563eb" }}
        >
          kontakt@fahrschul-kurse.ch
        </Link>
      </Text>
      <Text>
        Bei Abmeldungen, welche weniger als 4 Tage vor Kursbeginn erfolgen oder
        unentschuldigtem Nichterscheinen, wird der ganze Kursbeitrag in Rechnung
        gestellt.
      </Text>
    </View>
  );
};

const TrafficCoursePDF = ({
  data,
  cardSelectedValues,
  type,
}: {
  data: any; // or your formData type
  cardSelectedValues: any;
  type: "vku" | "plus" | null;
}) => {
  // The same logic to produce a "title"
  const title = `${type === "vku" ? "Verkehrskunde-Kurs" : "Nothelferkurse"} 
  Winterthur City, ${formatDateEmail(cardSelectedValues?.fDate)}`;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Title */}
        <Text style={styles.title}>Buchungsbestätigung {title}</Text>

        {/* Two-column layout for user address vs. company info */}
        <View style={styles.twoColumnGrid}>
          {/* Left column: user details */}
          <View style={styles.column}>
            <Text style={styles.textSmall}>
              {data?.firstName} {data?.lastName}
            </Text>
            <Text style={styles.textSmall}>{data?.streetAddress}</Text>
            <Text style={styles.textSmall}>
              {data?.postalCode} {data?.city}
            </Text>
            <Text style={styles.textSmall}>{data?.telNumber}</Text>
            <Text style={styles.textSmall}>{data?.email}</Text>
            <Text style={styles.textSmall}>{data?.bday}</Text>
            <Text style={styles.textSmall}>{data?.learnerSwissNumber}</Text>
          </View>

          {/* Right column: company details */}
          <View style={styles.column}>
            <Text style={styles.textSmall}>
              Fahrschul-Kurse{"\n"}
              www.fahrschul-kurse.ch
            </Text>
          </View>
        </View>

        {/* Summary Table */}
        <CourseInfo title={title} price={cardSelectedValues?.price} />

        {/* Participant Table */}
        <DriverDetails
          firstName={data?.firstName}
          lastName={data?.lastName}
          email={data?.email}
        />

        {/* Course Date/Time Info */}
        <CourseDateTime type={type} title={title} data={cardSelectedValues} />

        {/* Footer */}
        <Footer type={type} />
      </Page>
    </Document>
  );
};

export default TrafficCoursePDF;
